import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';

import './index.css';
import TransactionItemCandlesChart from './Candles';
import CandleView from './CandleView';
import { DetailCompactTransaction } from '../../../../../../types/subscription';
import {
  CbrtRangeState,
  MrcState,
  ProfitResultIndicatorConfigs,
  WakeupTimes,
} from '../../../../../ProfitCalculations/types';
import { BotTimeFrames } from '../../../../../Detail/types';
import { Bot } from 'grts-utils/src/types/Bot';
import { colors } from '../../../../../../colors';
import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../../../helpers/NumberFormatter';
import TransactionItemStructure from '../../../../../../components/TransactionItem';

interface EnhancedProps {
  index: number;
  transaction: DetailCompactTransaction;
  compactTransactions: DetailCompactTransaction[];
  compactTransactionIndex: number | null;
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: DetailCompactTransaction[];
  showCandles: boolean;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames: BotTimeFrames | undefined;
  showCurrentProfit: boolean;
  wakeupTimes?: WakeupTimes;
  bot: Bot | undefined;
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

interface Props {
  index: number;
  transaction: DetailCompactTransaction;
  compactTransactionIndex: number | null;
  compactTransactions: DetailCompactTransaction[];
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: DetailCompactTransaction[];
  toggleCompactTransaction?: () => void;
  isCompact?: boolean;
  isVeryFirst?: boolean;
  isFirstClusteredTransaction?: boolean;
  clusteredTransactionIndex?: number;
  totalClusteredTransactions?: number;
  showCandles: boolean;
  isChartView: boolean;
  setIsChartView: React.Dispatch<React.SetStateAction<boolean>>;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames: BotTimeFrames | undefined;
  showCurrentProfit: boolean;
  wakeupTimes?: WakeupTimes;
  bot: Bot | undefined;
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

const TransactionItem: React.FC<Props> = (props: Props) => {
  const {
    bot,
    transaction,
    compactTransactions,
    currentProfit,
    currentPriceChange,
    isCompact = true,
    isVeryFirst = false,
    showCandles,
    isChartView,
    setIsChartView,
    cbrtRangeStates,
    mrcStates,
    timeFrames,
    showCurrentProfit,
    wakeupTimes,
    clusteredTransactions,
    indicatorConfig,
  } = props;

  const transactionOrderColor = useCallback(
    (orderType: 'BUY' | 'SELL') =>
      orderType === 'BUY' ? colors.green : colors.red,
    [],
  );

  const getProfitValue = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return profit;
    }

    if (profit === null) {
      return formatPercentage(0);
    }

    if (typeof profit === 'string') {
      return formatPercentage(parseFloat(profit));
    }

    return formatPercentage(profit);
  }, []);

  const getProfitColor = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return colors.orange;
    }

    if (profit === null) {
      return colors.white;
    }

    if (typeof profit === 'string') {
      return parseFloat(profit) >= 0 ? colors.green : colors.red;
    }

    return profit >= 0 ? colors.green : colors.red;
  }, []);

  const transactionLabel = useMemo(() => {
    if (transaction.transactionType.includes('TAKE_PROFIT')) {
      return 'TP';
    }

    return typeof transaction.index !== 'undefined' ? transaction.index : 'x';
  }, [transaction.index, transaction.transactionType]);

  const handleCandlesPress = useCallback(() => {
    setIsChartView(true);
  }, [setIsChartView]);

  const handleCloseChartView = useCallback(() => {
    setIsChartView(false);
  }, [setIsChartView]);

  const transactionPrice = useMemo(() => {
    const price = transaction.averageOrderPrice || transaction.orderPrice || 0;

    return formatPrice(price);
  }, [transaction]);

  const shouldShowCurrentProfit =
    showCurrentProfit && (isVeryFirst || isCompact);

  return (
    <TransactionItemStructure
      circleLabel={transactionLabel}
      circleColor={transactionOrderColor(transaction.orderType)}
      date={moment(transaction.transactionTimestamp).format('D MMM YYYY HH:mm')}
      currentProfit={shouldShowCurrentProfit ? currentProfit : undefined}
      currentPriceChange={
        shouldShowCurrentProfit ? currentPriceChange : undefined
      }
      content={
        isChartView ? (
          <div
            style={{
              display: 'grid',
              gridTemplateRows: '1fr',
              gridTemplateColumns: '1fr',
              padding: 8,
              height: '60vh',
            }}
          >
            <CandleView
              bot={bot}
              isCompact={isCompact}
              transaction={transaction}
              compactTransactions={compactTransactions}
              clusteredTransactions={clusteredTransactions}
              handleCloseChartView={handleCloseChartView}
              cbrtRangeStates={cbrtRangeStates}
              mrcStates={mrcStates}
              timeFrames={timeFrames}
              wakeupTimes={wakeupTimes}
              indicatorConfig={indicatorConfig}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateRows: '1fr 1fr 1fr',
              gridTemplateColumns: '1fr 1fr 1fr',
              color: colors.white,
              padding: 8,
              paddingLeft: 32,
              paddingRight: 32,
            }}
          >
            <div
              style={{
                gridRow: '1 / 2',
                gridColumn: '1 / 3',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span style={{ color: colors.gray, fontSize: 22 }}>
                {isCompact ? 'Average Price:' : 'Price:'}
              </span>
              <span
                style={{ color: colors.white, fontSize: 26, marginLeft: 4 }}
              >
                {transactionPrice}
              </span>
            </div>
            <div
              style={{
                gridRow: '2 / 3',
                gridColumn: '1 / 2',
              }}
            >
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Stable quantity:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginLeft: 4 }}
                >
                  {formatBalance(transaction.stableQuantity, true)}
                </span>
              </div>
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Asset quantity:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginLeft: 4 }}
                >
                  {formatBalance(transaction.assetQuantity, false)}
                </span>
              </div>
            </div>
            <div
              style={{
                gridRow: '2 / 3',
                gridColumn: '2 / 3',
              }}
            >
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Stable profit:
                </span>
                <span
                  style={{
                    color:
                      transaction.orderType === 'SELL'
                        ? getProfitColor(transaction.profit)
                        : colors.white,
                    fontSize: 18,
                    marginLeft: 4,
                  }}
                >
                  {transaction.orderType === 'SELL'
                    ? getProfitValue(transaction.profit)
                    : '0%'}
                </span>
              </div>
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Asset profit:
                </span>
                <span
                  style={{
                    color:
                      transaction.orderType === 'BUY'
                        ? getProfitColor(transaction.profit)
                        : colors.white,
                    fontSize: 18,
                    marginLeft: 4,
                  }}
                >
                  {transaction.orderType === 'BUY'
                    ? getProfitValue(transaction.profit)
                    : '0%'}
                </span>
              </div>
            </div>
            <div
              style={{
                gridRow: '3 / 4',
                gridColumn: '1 / 2',
              }}
            >
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Stable balance:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginLeft: 4 }}
                >
                  {formatBalance(transaction.currentStableBalance, true)}
                </span>
              </div>
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Asset balance:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginLeft: 4 }}
                >
                  {formatBalance(transaction.currentAssetBalance, false)}
                </span>
              </div>
            </div>
            <div
              style={{
                gridRow: '3 / 4',
                gridColumn: '2 / 3',
              }}
            >
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Fee value
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginLeft: 4 }}
                >
                  {formatBalance(transaction.currentFee)}
                </span>
              </div>
              <div>
                <span style={{ color: colors.gray, fontSize: 16 }}>
                  Total fees paid:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginLeft: 4 }}
                >
                  {formatBalance(transaction.totalFee)}
                </span>
              </div>
            </div>
            <div
              style={{
                gridRow: '1 / 4',
                gridColumn: '3 / 4',
              }}
            >
              {showCandles && (
                <div
                  onClick={handleCandlesPress}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  {/** Candles */}
                  <TransactionItemCandlesChart
                    transaction={transaction}
                    compactTransactions={compactTransactions}
                  />
                </div>
              )}
            </div>
          </div>
        )
      }
    />
  );
};

const EnhancedTransactionItem: React.FC<EnhancedProps> = (
  props: EnhancedProps,
): any => {
  const { clusteredTransactions, compactTransactionIndex } = props;
  const [showCompactTransaction, setShowCompactTransaction] = useState(true);

  const [isChartView, setIsChartView] = useState(false);

  const toggleCompactTransaction = useCallback(() => {
    setShowCompactTransaction(!showCompactTransaction);
  }, [showCompactTransaction]);

  if (showCompactTransaction) {
    return (
      <TransactionItem
        {...props}
        toggleCompactTransaction={toggleCompactTransaction}
        isChartView={isChartView}
        setIsChartView={setIsChartView}
      />
    );
  }

  if (clusteredTransactions.length) {
    return clusteredTransactions.map((t, index) => {
      return (
        <TransactionItem
          key={t.id}
          {...props}
          transaction={t}
          isCompact={false}
          isVeryFirst={compactTransactionIndex === 0 && index === 0}
          toggleCompactTransaction={toggleCompactTransaction}
          isFirstClusteredTransaction={index === 0}
          clusteredTransactionIndex={index}
          totalClusteredTransactions={clusteredTransactions.length}
          isChartView={isChartView}
          setIsChartView={setIsChartView}
        />
      );
    });
  }

  return (
    <TransactionItem
      {...props}
      toggleCompactTransaction={toggleCompactTransaction}
      isChartView={isChartView}
      setIsChartView={setIsChartView}
    />
  );
};

export default EnhancedTransactionItem;
